import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import Footer from "./Footer";
import Header from "./Header";
import siteConfig, { formatPrice } from "../config/siteConfig";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [voucher, setVoucher] = useState("");
  const [voucherMessage, setVoucherMessage] = useState("");

  const detailedItems = useMemo(
    () => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const shipping = subtotal === 0 ? 0 : siteConfig.shippingFee;
  const total = subtotal + shipping;

  const applyVoucher = (event) => {
    event.preventDefault();
    setVoucherMessage(voucher.trim() ? "That code is not currently active." : "Enter a voucher code first.");
  };

  if (!detailedItems.length) {
    return (
      <div className="cart-page">
        <Header />
        <main className="cart-empty">
          <div className="cart-empty__icon"><FiShoppingBag /></div>
          <span className="eyebrow">Your bag</span>
          <h1>Nothing here yet.</h1>
          <p>Start with a pair you can wear for years. We’ll keep it here when you find it.</p>
          <Link to="/shop" className="app-button">Explore the collection</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header />
      <main className="cart-page__main">
        <header className="cart-page__heading">
          <span className="eyebrow">Shopping bag</span>
          <h1>Cart <sup>{detailedItems.reduce((totalCount, item) => totalCount + item.quantity, 0)}</sup></h1>
          <Link to="/shop">Continue shopping <FiArrowRight /></Link>
        </header>

        <section className="cart-page__items" aria-label="Cart items">
          {detailedItems.map((item) => (
            <article className="cart-item" key={`${item.id}-${item.size}-${item.color}`}>
              <Link to={`/shopDetails/${item.product.id}`} className="cart-item__image">
                <img src={item.product.image} alt={item.product.name} />
              </Link>
              <div className="cart-item__details">
                <div className="cart-item__title">
                  <Link to={`/shopDetails/${item.product.id}`}>{item.product.name}</Link>
                  <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                </div>
                <p>{item.color} · {item.size}</p>
                <p>{item.product.attributes.fit} fit</p>
                <div className="cart-item__bottom">
                  <div className="cart-item__quantity" aria-label={`Quantity for ${item.product.name}`}>
                    <button onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity - 1)} aria-label="Decrease quantity"><FiMinus /></button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)} aria-label="Increase quantity"><FiPlus /></button>
                  </div>
                  <button className="cart-item__remove" onClick={() => removeFromCart(item.id, item.size, item.color)}><FiTrash2 /> Remove</button>
                </div>
              </div>
            </article>
          ))}
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <p className="cart-summary__shipping-earned">Flat-rate tracked shipping: {formatPrice(siteConfig.shippingFee)}</p>
          <div className="cart-summary__rows">
            <p><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></p>
            <p><span>Shipping</span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong></p>
            <p className="cart-summary__total"><span>Total <small>{siteConfig.currency}</small></span><strong>{formatPrice(total)}</strong></p>
          </div>
          <button className="cart-summary__checkout" onClick={() => navigate("/checkout")}>Checkout <FiArrowRight /></button>
          <form className="cart-summary__voucher" onSubmit={applyVoucher}>
            <label htmlFor="voucher">Voucher code</label>
            <div><input id="voucher" value={voucher} onChange={(event) => setVoucher(event.target.value)} placeholder="Enter code" /><button type="submit">Apply</button></div>
            {voucherMessage && <p role="status">{voucherMessage}</p>}
          </form>
          <p className="cart-summary__note">External payment page · {siteConfig.returnWindowDays}-day return request window</p>
        </aside>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
